import React, { PureComponent, ReactElement } from 'react';
import { Mutation } from '@apollo/client/react/components';

import ErrorBoundary from 'buyside/components/presentation/ErrorBoundary/errorBoundary';
import LoadingSpinner, {
  SIZES
} from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { get } from 'buyside/util/lodashAltJs';

import {
  GoogleReCaptchaDocument,
  GoogleReCaptchaMutation,
  GoogleReCaptchaMutationVariables
} from 'interfaces/graphql-typings';
export interface ReCAPTCHAMutationChildrenProps {
  grecaptcha: () => Promise<unknown>;
}
interface ContainerProps {
  children: (props: ReCAPTCHAMutationChildrenProps) => ReactElement;
  pageName: string;
  action: string;
}

const SITE_KEY = window.grecaptcha_site_key;
const preloadingScriptId = 'grecaptchaPreloadingScript';

// Inspiration: https://codesandbox.io/s/0m5omor41n?from-embed - SHK 8/22/19
class ReCAPTCHAMutationContainer extends PureComponent<ContainerProps, { isReady: boolean }> {
  constructor(props: ContainerProps) {
    super(props);
    this.state = {
      isReady: false
    };
    this.loadScript = this.loadScript.bind(this);
    this.onLoad = this.onLoad.bind(this);
  }

  componentDidMount(): void {
    this.loadScript();
  }

  componentWillUnmount(): void {
    const node = document.getElementById(preloadingScriptId);
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  /* onload=captchaOnLoad prevents race-conditions with the api.js
  Once the api.js library has loaded, the captchaOnLoad (i.e., this.onLoad) function will run
  The combination of loadScript and onLoad prevents the error that ".ready does not exist"
  This allows us to always use the exported grecaptcha() function, even on componentDidMount
  - SHK 8/22/19 */
  loadScript(): void {
    if (!document.getElementById(preloadingScriptId)) {
      window.captchaOnLoad = this.onLoad;
      const script = document.createElement('script');
      script.id = preloadingScriptId;
      script.type = 'text/javascript';
      script.src = `https://www.google.com/recaptcha/api.js?onload=captchaOnLoad&render=${SITE_KEY}`;
      document.head.appendChild(script);
    }
  }

  onLoad(): void {
    window.grecaptcha.ready((): void => {
      this.setState({ isReady: true });
    });
  }

  render(): ReactElement {
    const { isReady } = this.state;
    const { pageName, action, children } = this.props;
    return (
      <ErrorBoundary pageName={pageName}>
        <Mutation<GoogleReCaptchaMutation, GoogleReCaptchaMutationVariables>
          mutation={GoogleReCaptchaDocument}
        >
          {(googleReCAPTCHA, { error }): ReactElement => {
            if (error) {
              throw error;
            }

            const grecaptcha = (): Promise<boolean | void> => {
              return new Promise((resolve, reject): void => {
                window.grecaptcha
                  .execute(SITE_KEY, { action: action })
                  .then((response: string): void => {
                    googleReCAPTCHA({ variables: { response, action } })
                      .then((value): void => {
                        const isNotBot = get(value, ['data', 'googleReCAPTCHA', 'verified']);
                        if (isNotBot) {
                          resolve(true);
                        } else {
                          reject();
                        }
                      })
                      .catch(() => {
                        throw error;
                      });
                  });
              });
            };

            if (isReady) {
              return children({ grecaptcha });
            } else {
              return <LoadingSpinner size={SIZES.MEDIUM} />;
            }
          }}
        </Mutation>
      </ErrorBoundary>
    );
  }
}

export default ReCAPTCHAMutationContainer;
